import { formatEta } from '../../lib/format.ts';
import { connectionLabel, type ConnectionLabelState } from '../ops/connectionStatus.ts';
import type { MessageKey } from '../../i18n/messages.en.ts';

/**
 * P1 fix (round 6 critic): pure decision logic for `RequestPage.tsx`'s realtime-frame-triggered
 * refetch of this device's own request status (`GET /api/requests/mine?deviceId=`).
 *
 * The backend now publishes two shapes a guest's own `['public', 'requests']` subscription can
 * see (server-side confirmed by reading every `publish(...)` call in the codebase):
 *  - `{channel:'public', event:'queue.changed', payload:{session}}` — `queue-entry`'s lifecycle
 *    hooks (`src/api/queue-entry/content-types/queue-entry/lifecycles.js`) fire this on ANY
 *    queue-entry mutation in the session (accept, advance, skip, remove) via the shared
 *    `queue-broadcast.js` debounce, deliberately carrying no queue contents — a guest must never
 *    see other singers' rows, this is just a "go re-fetch" nudge. `session.updated` is the other
 *    (unrelated) event this channel carries; matched by channel alone below, forward-compatible
 *    with whatever else ever flows over `public`.
 *  - `{channel:'requests', event:'request.mine', payload:<GuestRequestView>}` — pushed straight at
 *    this device (`song-request` service's `pushMineUpdate`/`broadcastPendingUpdates`) with an
 *    already-computed guest view. R8-P1-2 (round 8 critic, reproduced live): trusting that payload
 *    directly and skipping the refetch is fragile — any gap between what a given push call
 *    happened to populate and what `GET /api/requests/mine` recomputes fresh shows up as a stale
 *    or degraded card with no way to self-correct. Treating this as ANOTHER refetch trigger (not
 *    a replacement for one) makes `GET /api/requests/mine` the single source of truth every time,
 *    while the frame itself still lets `RequestPage` merge in whatever it *did* carry immediately,
 *    without waiting out the debounce, for the fast-feeling update AC-REQ-8 asks for.
 *
 * `RequestPage.tsx` debounces the actual network call (500ms) since a burst of frames — e.g. an
 * advance's `queue.changed` immediately followed by this device's own `request.mine` — should
 * only ever cost one fetch.
 */
export function shouldRefetchMineOnFrame(frame: { channel: string; event?: string }): boolean {
  if (frame.channel === 'public') return true;
  if (frame.channel === 'requests' && frame.event === 'request.mine') return true;
  return false;
}

// ---------------------------------------------------------------------------------------------
// R11-P2-3 fix: single coalescing scheduler for GET /api/requests/mine
// ---------------------------------------------------------------------------------------------

export interface MineRefetchSchedulerState {
  /** Wall-clock ms the most recently started fetch finished, or `null` before this scheduler has
   * ever run one. */
  lastCompletedAt: number | null;
  /** True from the moment a fetch this scheduler kicked off starts until it settles. */
  fetchInFlight: boolean;
  /** True while a trailing fetch is already queued for later — a further trigger arriving before
   * it fires must not queue a second one. */
  trailingScheduled: boolean;
}

export const INITIAL_MINE_REFETCH_SCHEDULER_STATE: MineRefetchSchedulerState = {
  lastCompletedAt: null,
  fetchInFlight: false,
  trailingScheduled: false,
};

export type MineRefetchPlan = { action: 'fetch-now' } | { action: 'schedule-trailing'; delayMs: number } | { action: 'noop' };

/** Any two fetches this scheduler runs must be at least this far apart — chosen to comfortably
 * clear the ~2s gap observed live between a queue change's two frames (see doc comment below)
 * while still refreshing well within a guest's patience.
 *
 * R12 residue fix: 1500ms was tuned to the ~2s gap seen in one live sample, but a later repro
 * showed the same advance's two frames landing anywhere from ~2s to ~2.4s apart — close enough to
 * the old window that a trigger arriving at, say, +1.6s after the first still fell outside it and
 * cost a second immediate fetch. Widened to 3000ms so any real-world gap in that observed range
 * lands safely inside a single window, while still comfortably clearing a guest's patience for a
 * "just requested" update. */
export const MINE_REFETCH_COALESCE_WINDOW_MS = 3000;

/**
 * R11-P2-3 live defect (round 11 critic, reproduced live): a single queue-entry mutation fires TWO
 * separate realtime frames a guest's own subscription can see — `queue-broadcast.js`'s
 * `{channel:'public', event:'queue.changed'}` and, for the device whose own request the change
 * affects, `song-request` service's `{channel:'requests', event:'request.mine'}` push — observed
 * live landing roughly 2s apart, not simultaneously. `shouldRefetchMineOnFrame` correctly treats
 * both as "go refetch", but `RequestPage`'s OLD implementation gave each matching frame its own
 * fresh `setTimeout(refetchMine, 500ms)`, cleared and reset every time a NEW matching frame
 * arrived: that only coalesces frames landing within 500ms of EACH OTHER. Two frames ~2s apart
 * each got their own timer, so one queue change cost 2 network calls, confirmed live as
 * `GET /api/requests/mine` pairs at +0s and +2s on every transition.
 *
 * This function replaces that per-frame timer with a single decision point every trigger (a
 * matching frame, the `hello` reconnect signal, `visibilitychange`, the 15s poll fallback) routes
 * through, so at most ONE fetch is ever in flight or queued at once:
 *  - nothing in flight or queued, and either this is the first fetch ever or the last one
 *    completed `windowMs`+ ago -> fetch right now.
 *  - a fetch is in flight, or completed less than `windowMs` ago -> queue (at most) one trailing
 *    fetch for whenever the window closes, never a second one stacked behind an existing trailing
 *    fetch.
 * `RequestPage` owns the actual timer/promise plumbing (see its `scheduleMineRefetch`); this only
 * computes what to do next from the current state and a clock reading, so it is fully testable as
 * a pure function with a fake clock, no timers or a DOM required.
 */
export function planMineRefetch(
  state: MineRefetchSchedulerState,
  now: number,
  windowMs: number = MINE_REFETCH_COALESCE_WINDOW_MS
): MineRefetchPlan {
  if (state.fetchInFlight || state.trailingScheduled) return { action: 'noop' };
  if (state.lastCompletedAt === null) return { action: 'fetch-now' };
  const elapsed = now - state.lastCompletedAt;
  if (elapsed >= windowMs) return { action: 'fetch-now' };
  return { action: 'schedule-trailing', delayMs: windowMs - elapsed };
}

/**
 * R8-P1-2: merges a `request.mine` frame's payload onto whatever this device already knows about
 * its own request, rather than letting `setActiveRequest` blindly replace it. Every field on
 * `GuestRequestView` besides `position`/`etaSec` is unconditionally present on every server
 * response (`mapForGuest()`'s `base` object) — those two are the only ones ever genuinely absent
 * depending on status, so they are the only ones this ever falls back to `prev` for. A `status`
 * change (e.g. `accepted` -> `rejected`) always wins outright: `prev` is only ever consulted for
 * the numeric fields, never used to resurrect a stale status the new frame no longer reports.
 */
export function mergeMineFrame<T extends { status: string; position?: number; etaSec?: number }>(
  prev: { position?: number; etaSec?: number } | null,
  incoming: T
): T {
  return {
    ...incoming,
    position: incoming.position ?? prev?.position,
    etaSec: incoming.etaSec ?? prev?.etaSec,
  };
}

/**
 * R9-P1-D fix (round 9 critic, reproduced live): the guest page's own `/api/requests/mine` card
 * was fed ONLY by SSE `request.mine` frames — when the stream died server-side (reconnects
 * returning 403 for 4+ minutes, straight off a live repro), the card froze forever with no visible
 * sign anything was wrong. `RequestPage`'s 15s poll (`MINE_POLL_INTERVAL_MS`) now runs regardless
 * of realtime connection state (it never depended on it in the first place — see that file's own
 * comment), but a guest staring at a frozen-looking card with no realtime updates flowing still
 * deserves to know WHY nothing is moving, rather than reading it as silent success. Kept here
 * (not inline in the component) so the exact states that count as "live updates aren't flowing
 * right now" are unit-testable without a DOM or a real `RealtimeClient`.
 */
export function shouldShowLiveUpdatesHint(connectionState: string): boolean {
  return connectionState === 'degraded' || connectionState === 'offline' || connectionState === 'reauth';
}

/**
 * MVP-11 #16 cross-package contract: the guest card no longer invents its own "Live updates
 * paused — refreshing every 15s" phrasing — it reduces `RequestPage`'s own `ConnectionState`
 * (`api/realtime.ts`, 5 states) down to `connectionLabel`'s 4-state vocabulary
 * (`features/ops/connectionStatus.ts`, Package E's cross-package export) and reuses ITS copy, the
 * one DESIGN.md §6.4 specifies for the whole app. Returns null for every state
 * `shouldShowLiveUpdatesHint` already treats as "nothing to say" (`connecting`/`online`).
 */
export function guestConnectionHint(connectionState: string): MessageKey | null {
  if (!shouldShowLiveUpdatesHint(connectionState)) return null;
  // MVP-11 #4: SSE 'offline' means the realtime stream dropped, not that REST is unreachable —
  // `connectionLabel('offline')` is the §6.4 danger state reserved for REST itself being down, so
  // both 'degraded' and 'offline' map to the same 'reconnecting' copy here.
  const mapped: ConnectionLabelState =
    connectionState === 'degraded' || connectionState === 'offline' ? 'reconnecting' : (connectionState as 'reauth');
  // MVP-272: the KEY, so `RequestPage` renders it in the guest's own language. This function was
  // the reason the shared vocabulary had to move — Phase 1 translated everything around this card
  // and left the one string it borrows from another package in English.
  return connectionLabel(mapped).titleKey;
}

/**
 * MVP-11 #9 — the 15s `GET /api/requests/mine` poll (`RequestPage.tsx`'s `MINE_POLL_INTERVAL_MS`
 * effect) used to run unconditionally the whole time this device has a live request, layered on
 * top of realtime regardless of whether realtime was actually working. The server now pushes a
 * device-scoped `request.mine` frame on both accept and reject (song-request controller's
 * `accept`/`reject`, `:~300`/`:~350`), so every poll this removes already has a live frame behind
 * it — the poll only needs to cover the gap while THIS device's own connection genuinely is not
 * delivering frames. `'online'` is the only `ConnectionState` that means frames are flowing;
 * every other state (`connecting`/`degraded`/`offline`/`reauth`) can miss a push, so the fallback
 * stays armed through all of them — same rule Package E applies to the console's own polls.
 */
export function shouldPollWhileDisconnected(connectionState: string): boolean {
  return connectionState !== 'online';
}

/**
 * R10-P2-3(a) live defect: `etaSec` is a snapshot computed once, at the moment the server built
 * this payload (song-request service's `computePositionAndEta`, called once per
 * `GET /api/requests/mine` or `request.mine` push) — it never itself counts down, so a guest
 * whose ahead-of-them song just keeps playing saw "about 1:14 to go" frozen for the whole wait,
 * confirmed live. `RequestPage` records `fetchedAt` (its own `Date.now()`) alongside whichever
 * `GuestRequestView` it just received; `QueuePositionCard` calls this on a 1s tick to derive what
 * to actually show — the snapshot minus however long has elapsed since it was fetched, floored at
 * zero (never negative, never implying the wait is over before the server itself says so).
 */
export function computeDisplayEtaSec(etaSec: number, fetchedAt: number, now: number): number {
  return Math.max(0, etaSec - (now - fetchedAt) / 1000);
}

/**
 * R11-P2-3 / R10-P2-3 follow-up (round 11 critic, reproduced live): even with the backend fix
 * (song-request service's `computePositionAndEta` now subtracts elapsed time on the entry
 * currently playing), the server's `etaSec` is still only ever a coarse, whole-song-durations
 * estimate recomputed fresh on every call — nothing guarantees two consecutive computations 15s
 * apart, for the SAME position, agree to the second. Before this fix, a guest ticking the card
 * down 1s/s client-side (`computeDisplayEtaSec` above) would see the number jump back UP the
 * instant a refetch landed with a slightly larger fresh estimate, even though real time only ever
 * moves forward. `QueuePositionCard` calls this whenever a fresh `GuestRequestView` arrives (a new
 * `fetchedAt`), to decide what "server etaSec" to actually treat as the new countdown baseline:
 *  - same `position` as before -> the guest has not moved in line, so the display must never
 *    increase — take whichever is SMALLER: the fresh server value, or wherever the locally-ticked
 *    display had already counted down to.
 *  - a different (or newly-known) `position` -> something genuinely changed (moved up, or this is
 *    the first estimate ever seen) — the fresh server value always wins outright, even if it is
 *    larger (e.g. someone with 'next' priority just queue-jumped ahead of this guest).
 * Pure and side-effect free so it is testable without a DOM/timers — `QueuePositionCard` is the
 * only caller, inside a `useEffect` keyed on the incoming `(position, etaSec, fetchedAt)`.
 */
export function reconcileDisplayEtaOnRefetch(params: {
  prevPosition: number | undefined;
  /** The previously-displayed value, already ticked down to "now" (e.g. via
   * `computeDisplayEtaSec` against the OLD baseline) — undefined when there is no prior baseline
   * yet (first fetch this page has ever seen). */
  prevDisplayEtaSec: number | undefined;
  nextPosition: number | undefined;
  nextEtaSec: number | undefined;
}): number | undefined {
  const { prevPosition, prevDisplayEtaSec, nextPosition, nextEtaSec } = params;
  if (typeof nextEtaSec !== 'number') return nextEtaSec;
  if (prevPosition === nextPosition && typeof prevDisplayEtaSec === 'number') {
    return Math.min(prevDisplayEtaSec, nextEtaSec);
  }
  return nextEtaSec;
}

/** Pure key+params result for `describeQueuePosition` — same shape as `requestErrors.ts`'s
 * `RequestErrorCopy`/`guestSongRowLabel.ts`'s `GuestSongRowLabel`: a pure module has no dictionary
 * to render a sentence with, so it hands back what to resolve rather than resolved text. */
export interface QueuePositionDescription {
  key: MessageKey;
  params?: Record<string, string | number>;
}

/**
 * Pure copy selection for `QueuePositionCard` — kept in this `.ts` module (not the `.tsx`
 * component itself) so `describeQueuePosition.test.ts`/`node --test` can exercise it directly
 * without a JSX transform. `mapForGuest()`'s own shape guarantees `position`/`etaSec` travel
 * together (song-request service: both set on the `accepted` + still-queued branch, both absent
 * otherwise) — this only checks one because TypeScript cannot see that invariant across the wire.
 *
 * `displayEtaSec` is the caller's already-ticked-down value (`computeDisplayEtaSec` above /
 * `QueuePositionCard`'s 1s-tick effect), never the request's raw `etaSec` directly — the raw
 * field is a snapshot from whenever the server last computed it, not a live countdown.
 *
 * R10-P2-3(b) live defect: when a guest's own entry IS the one currently playing,
 * `computePositionAndEta` alone reports `position: 1, etaSec: 0` — indistinguishable on the wire
 * from "about to go up next" — so the card read "#1 in line · ~0:00 to go" while the guest
 * was already meant to be on stage. `request.nowPlaying` (song-request service's `mapForGuest`)
 * is the signal that disambiguates it, and wins outright over the position/eta copy.
 *
 * MVP-272 Phase 1: this used to return the rendered English sentence directly. It now returns a
 * `messages.en.ts`/`messages.th.ts` key (+ params) like every other pure module in this feature —
 * `QueuePositionCard` (which already has `useT()`) resolves it via `t(key, params)`, and
 * `requestErrors.ts`'s `DUPLICATE_REQUEST` branch (the other caller) now threads the key straight
 * through as its own `detailKey` instead of piping the old English string through the
 * `guest.errors.serverMessage` passthrough.
 */
export function describeQueuePosition(
  request: {
    status: 'pending' | 'accepted' | 'played' | 'rejected' | 'expired';
    position?: number;
    nowPlaying?: boolean;
    rejectReason?: string | null;
  },
  displayEtaSec?: number
): QueuePositionDescription {
  switch (request.status) {
    case 'pending':
      return { key: 'guest.queue.pending' };
    case 'accepted':
      if (request.nowPlaying) return { key: 'guest.queue.nowPlaying' };
      return typeof request.position === 'number' && typeof displayEtaSec === 'number'
        ? { key: 'guest.queue.inLine', params: { position: request.position, eta: formatEta(displayEtaSec) } } // MVP-11 #17: one ETA rendering
        : { key: 'guest.queue.acceptedFallback' };
    case 'played':
      return { key: 'guest.queue.played' };
    case 'rejected':
      return request.rejectReason
        ? { key: 'guest.queue.rejectedWithReason', params: { reason: request.rejectReason } }
        : { key: 'guest.queue.rejectedFallback' };
    case 'expired':
      return { key: 'guest.queue.expired' };
  }
}
